import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { useUserStore } from '../stores/userStore';
import { BusinessApiService } from '../api/businessApi';

interface NavItem {
  labelKey: string;
  path: string;
  locked?: boolean;
  lockReasonKey?: string;
}

const workerNavItems: NavItem[] = [
  { labelKey: 'nav.home', path: '/dashboard' },
  { labelKey: 'nav.discover', path: '/discover' },
  { labelKey: 'nav.community', path: '/discover/community' },
  { labelKey: 'nav.connect', path: '/connect' },
  { labelKey: 'nav.help', path: '/community-help' },
  { labelKey: 'nav.meetups', path: '/meetups' },
  { labelKey: 'nav.marketplace', path: '/marketplace' },
  { labelKey: 'nav.tips_trips', path: '/tips-trips-advice' },
  { labelKey: 'nav.jobs', path: '/search-jobs' },
  // { labelKey: 'nav.perks', path: '/referrals' },
  { labelKey: 'nav.what_to_know', path: '/what-to-know' },
  { labelKey: 'nav.chat', path: '/chat' },
];

const buildEmployerNavItems = (
  businessId: number | null,
  businessStatus: string | null,
  employerPlan: string | null,
): NavItem[] => {
  const isPending = businessStatus !== 'approved';
  const isStandardPlus = ['standard', 'pro', 'founding'].includes(employerPlan || '');
  const isProPlus = ['pro', 'founding'].includes(employerPlan || '');

  return [
    { labelKey: 'nav.dashboard', path: '/employer-dashboard' },
    {
      labelKey: 'nav.post_a_job',
      path: '/post-job',
      locked: isPending,
      lockReasonKey: isPending ? 'nav.lock_pending' : undefined,
    },
    {
      labelKey: 'nav.browse_workers',
      path: '/employer-browse-workers',
      locked: isPending || !isStandardPlus,
      lockReasonKey: isPending ? 'nav.lock_pending' : 'nav.upgrade_standard',
    },
    {
      labelKey: 'nav.network',
      path: '/employer-network',
      locked: isPending || !isStandardPlus,
      lockReasonKey: isPending ? 'nav.lock_pending' : 'nav.upgrade_standard',
    },
    {
      labelKey: 'nav.company_profile',
      path: businessId ? `/edit-business/${businessId}` : '/employer-profile',
    },
    {
      labelKey: 'nav.reviews',
      path: '/employer/my-reviews',
      locked: isPending || !isProPlus,
      lockReasonKey: isPending ? 'nav.lock_pending' : 'nav.upgrade_pro',
    },
  ];
};

const DesktopNav: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [businessId, setBusinessId] = useState<number | null>(null);
  const [businessStatus, setBusinessStatus] = useState<string | null>(null);
  const [businessPlan, setBusinessPlan] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.is_employer) return;
    let cancelled = false;
    BusinessApiService.getUserBusinesses()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data && res.data.length > 0) {
          const primary = res.data.find((b) => b.status === 'approved') || res.data[0];
          setBusinessId(primary.id);
          setBusinessStatus(primary.status);
          setBusinessPlan(primary.employer_plan ?? null);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.is_employer]);

  const navItems: NavItem[] = user?.is_employer
    ? buildEmployerNavItems(businessId, businessStatus, businessPlan)
    : workerNavItems;

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/employer-dashboard') return location.pathname === '/employer-dashboard';
    if (path.startsWith('/edit-business/')) return location.pathname.startsWith('/edit-business/');
    if (path.startsWith('/company-reviews/')) return location.pathname.startsWith('/company-reviews/');
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <nav className="hidden md:block bg-white border-b border-gray-200 sticky top-0 z-30">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
          {navItems.map((item) =>
            item.locked ? (
              <span
                key={item.labelKey}
                title={item.lockReasonKey ? t(item.lockReasonKey) : t('nav.locked')}
                className="whitespace-nowrap px-3 py-1.5 rounded-md text-sm font-medium text-gray-300 cursor-not-allowed flex items-center gap-1"
              >
                <Lock className="w-3.5 h-3.5" />
                {t(item.labelKey)}
              </span>
            ) : (
              <Link
                key={item.labelKey}
                to={item.path}
                className={`whitespace-nowrap px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-blue-50 text-[#0085FF]'
                    : 'text-gray-600 hover:text-[#0085FF] hover:bg-gray-50'
                }`}
              >
                {t(item.labelKey)}
              </Link>
            )
          )}
        </div>
      </div>
    </nav>
  );
};

export default DesktopNav;
